import React, { useEffect, useState } from 'react';
import Toast from '../../../components/ui/Toast';
import { getImageUrl } from '../../../utils/config';
import { formatPrice, getCurrencySymbol } from '../../../utils/currency';

const ProductForm = ({ 
  product = null, 
  categories = [],
  store = null,
  onSubmit, 
  onCancel, 
  isSubmitting = false 
}) => {
  const [formData, setFormData] = useState({
    category_id: '',
    store_id: '',
    name: '',
    price: '',
    discount_percentage: '',
    details: '',
    image: null,
    remove_image: false
  });
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    if (product) {
      setFormData({
        category_id: product.category_id?.toString() || '',
        store_id: product.store_id?.toString() || store?.id?.toString() || '',
        name: product.name || '',
        price: product.price?.toString() || '',
        discount_percentage: product.discount_percentage?.toString() || '',
        details: product.details || '',
        image: null,
        remove_image: false
      });
    } else if (store) {
      setFormData(prev => ({
        ...prev,
        store_id: store.id?.toString() || ''
      }));
    }
  }, [product, store]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        showToast('Please select a valid image file (JPG, JPEG, or PNG)', 'error');
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        showToast('Image size should be less than 2MB', 'error');
        return;
      }

      setFormData(prev => ({
        ...prev,
        image: file,
        remove_image: false // Reset remove_image when new file is selected
      }));
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      image: null,
      remove_image: true
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // For create: required fields
    if (!product) {
      if (!formData.category_id) {
        newErrors.category_id = 'Category is required';
      }
      if (!formData.name.trim()) {
        newErrors.name = 'Product name is required';
      }
      if (!formData.price || parseFloat(formData.price) < 0) {
        newErrors.price = 'Valid price is required';
      }
    }

    // For both create and update: validation
    if (formData.name.trim() && formData.name.trim().length > 255) {
      newErrors.name = 'Product name must not exceed 255 characters';
    }

    if (formData.price && (isNaN(parseFloat(formData.price)) || parseFloat(formData.price) < 0)) {
      newErrors.price = 'Price must be a valid number greater than or equal to 0';
    }

    if (formData.discount_percentage && (isNaN(parseFloat(formData.discount_percentage)) || parseFloat(formData.discount_percentage) < 0 || parseFloat(formData.discount_percentage) > 100)) {
      newErrors.discount_percentage = 'Discount percentage must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const formDataToSend = new FormData();
      
      // Always send store_id
      if (formData.store_id) {
        formDataToSend.append('store_id', formData.store_id);
      }
      
      // Only send fields if they have values
      if (formData.category_id) {
        formDataToSend.append('category_id', formData.category_id);
      }
      if (formData.name.trim()) {
        formDataToSend.append('name', formData.name.trim());
      }
      if (formData.price) {
        formDataToSend.append('price', formData.price);
      }
      if (formData.discount_percentage) {
        formDataToSend.append('discount_percentage', formData.discount_percentage);
      }
      if (formData.details) {
        formDataToSend.append('details', formData.details.trim());
      }
      
      // Send image if selected
      if (formData.image && formData.image instanceof File) {
        formDataToSend.append('image', formData.image);
      }

      // Send remove_image flag if true
      if (formData.remove_image) {
        formDataToSend.append('remove_image', '1');
      }

      await onSubmit(formDataToSend);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };

  const calculateDiscountedPrice = () => {
    if (formData.price && formData.discount_percentage) {
      const price = parseFloat(formData.price);
      const discount = parseFloat(formData.discount_percentage);
      return price - (price * discount / 100);
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="mb-6 relative">
        <button
          type="button"
          onClick={onCancel}
          className="absolute top-0 right-0 text-gray-400 hover:text-gray-600 transition-colors"
          disabled={isSubmitting}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h2 className="text-xl font-semibold text-gray-900">
          {product ? 'Edit Product' : 'Create New Product'}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {product ? 'Update the product information below.' : `Add a new product to ${store?.name || 'your store'}.`}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Category Selection */}
        <div>
          <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 mb-2">
            Category {!product && '*'}
          </label>
          <select
            id="category_id"
            name="category_id"
            value={formData.category_id}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
              errors.category_id ? 'border-red-300' : 'border-gray-300'
            }`}
            disabled={isSubmitting}
          >
            <option value="">Select a category</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          {errors.category_id && (
            <p className="mt-1 text-sm text-red-600">{errors.category_id}</p>
          )}
        </div>

        {/* Product Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Product Name {!product && '*'}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
              errors.name ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter product name"
            disabled={isSubmitting}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {/* Price and Discount */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
              Price {!product && '*'}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                {getCurrencySymbol(store?.currency)}
              </span>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                className={`w-full px-4 py-3 pl-12 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.price ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="0.00"
                disabled={isSubmitting}
              />
            </div>
            {errors.price && (
              <p className="mt-1 text-sm text-red-600">{errors.price}</p>
            )}
          </div>

          <div>
            <label htmlFor="discount_percentage" className="block text-sm font-medium text-gray-700 mb-2">
              Discount Percentage
            </label>
            <div className="relative">
              <input
                type="number"
                id="discount_percentage"
                name="discount_percentage"
                value={formData.discount_percentage}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                max="100"
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.discount_percentage ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="0"
                disabled={isSubmitting}
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
            </div>
            {errors.discount_percentage && (
              <p className="mt-1 text-sm text-red-600">{errors.discount_percentage}</p>
            )}
            {calculateDiscountedPrice() && (
              <p className="mt-1 text-sm text-green-600">
                Final price: {formatPrice(calculateDiscountedPrice(), store?.currency)}
              </p>
            )}
          </div>
        </div>

        {/* Product Details */}
        <div>
          <label htmlFor="details" className="block text-sm font-medium text-gray-700 mb-2">
            Product Details
          </label>
          <textarea
            id="details"
            name="details"
            value={formData.details}
            onChange={handleInputChange}
            rows={4}
            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
              errors.details ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter product details"
            disabled={isSubmitting}
          />
          {errors.details && (
            <p className="mt-1 text-sm text-red-600">{errors.details}</p>
          )}
        </div>

        {/* Product Image */}
        <div>
          <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-2">
            Product Image (Optional)
          </label>
          <div className="space-y-3">
            <input
              type="file"
              id="image"
              name="image"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleFileChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              disabled={isSubmitting}
            />
            
            {/* Current Image Display */}
            {product?.image && !formData.image && !formData.remove_image && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <img
                    src={getImageUrl(product.image)}
                    alt="Current image"
                    className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                  />
                  <span className="text-sm text-gray-600">Current image</span>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                >
                  Remove
                </button>
              </div>
            )}

            {/* New Image Preview */}
            {formData.image && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <img
                    src={URL.createObjectURL(formData.image)}
                    alt="Preview"
                    className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                  />
                  <span className="text-sm text-gray-600">{formData.image.name}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, image: null }))}
                  className="px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                >
                  Remove
                </button>
              </div>
            )}

            {/* Remove Image Checkbox (for backend) */}
            {formData.remove_image && (
              <div className="flex items-center space-x-2 p-3 bg-red-50 rounded-lg">
                <input
                  type="checkbox"
                  id="remove_image"
                  name="remove_image"
                  checked={formData.remove_image}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <label htmlFor="remove_image" className="text-sm text-red-700">
                  Remove current image
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {product ? 'Updating...' : 'Creating...'}
              </div>
            ) : (
              product ? 'Update Product' : 'Create Product'
            )}
          </button>
          
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            Cancel
          </button>
        </div>
      </form>

      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={closeToast}
      />
    </div>
  );
};

export default ProductForm; 